import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogOverlay, DialogPortal } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, Cookie } from 'lucide-react';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useAppData } from '@/contexts/AppDataContext';
import { PrivacyPolicyContent } from '@/components/PrivacyPolicyContent';
import { TermsOfUseContent } from '@/components/TermsOfUseContent';
import { cn } from '@/lib/utils';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { useTheme } from '@/contexts/ThemeContext';

interface CookieConsentProps {
  config: any;
}

// Custom Dialog components with higher z-index
const HighZIndexDialogContent = ({ className, children, ...props }: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>) => (
  <DialogPortal>
    <DialogPrimitive.Overlay
      className="fixed inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      style={{ zIndex: 10003 }}
    />
    <DialogPrimitive.Content
      className={cn(
        "fixed left-[50%] top-0 md:top-[50%] grid w-full max-w-lg translate-x-[-50%] md:translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      style={{ zIndex: 10004 }}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
);

export const CookieConsent = ({ config }: CookieConsentProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);

  const { colors } = useTheme();
  const primaryColor = colors.primary;

  useEffect(() => {
    // Check if user has already accepted cookies
    const checkCookieConsent = async () => {
      const cookieConsent = await secureGetItem('cookieConsent');
      if (!cookieConsent) {
        // Show popup after a short delay
        const timer = setTimeout(() => {
          setIsVisible(true);
          setIsAnimating(true);
        }, 2000);
        return () => clearTimeout(timer);
      }
    };

    checkCookieConsent();
  }, []);

  const handleAccept = async () => {
    await secureSetItem('cookieConsent', 'accepted');
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('cookieConsentUpdated'));
    }, 300);
  };

  const handleDecline = async () => {
    await secureSetItem('cookieConsent', 'declined');
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('cookieConsentUpdated'));
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop - covers entire page including header */}
      {/* <div
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'
          }`}
        style={{ zIndex: 9998 }}
      /> */}

      {/* Cookie Consent Popup - Centered Bottom */}
      <div
        className={`fixed bottom-0 left-0 right-0 flex items-end justify-center p-4 md:p-6 transition-all duration-500 ${
          isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
        style={{ zIndex: 10002 }}
      >
        <Card className="w-full max-w-2xl shadow-2xl border-2 border-border/50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Icon Section */}
              <div className="flex-shrink-0">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: `${primaryColor}15` }}
                >
                  <Cookie className="h-8 w-8" style={{ color: primaryColor }} />
                </div>
              </div>

              {/* Content Section */}
              <div className="flex-1 min-w-0">
                <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3">
                  Política de Cookies
                </h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-6">
                  Utilizamos cookies para melhorar sua experiência, personalizar conteúdo e analisar o tráfego. 
                  Ao continuar navegando, você concorda com nossa{' '}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setPrivacyModalOpen(true);
                    }}
                    className="font-semibold hover:underline cursor-pointer transition-colors"
                    style={{ color: primaryColor }}
                  >
                    Política de Privacidade
                  </button>
                  {' '}e{' '}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setTermsModalOpen(true);
                    }}
                    className="font-semibold hover:underline cursor-pointer transition-colors"
                    style={{ color: primaryColor }}
                  >
                    Termos de Uso
                  </button>
                  .
                </p>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    size="lg"
                    onClick={handleAccept}
                    className="flex-1 sm:flex-initial sm:min-w-[140px] p-2 font-semibold text-white shadow-lg hover:shadow-xl transition-all"
                    style={{ 
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` 
                    }}
                  >
                    Aceitar
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={handleDecline}
                    className="flex-1 sm:flex-initial sm:min-w-[140px] p-2 font-medium border-2 hover:bg-muted/50"
                  >
                    Recusar
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Privacy Policy Modal */}
      <Dialog open={privacyModalOpen} onOpenChange={setPrivacyModalOpen}>
        <HighZIndexDialogContent className="max-w-4xl h-[90vh] p-0 flex flex-col">
          <DialogHeader className="p-6 pb-4 border-b flex-shrink-0">
            <DialogTitle>Política de Privacidade</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-6">
            <PrivacyPolicyContent />
          </div>
        </HighZIndexDialogContent>
      </Dialog>

      {/* Terms of Use Modal */}
      <Dialog open={termsModalOpen} onOpenChange={setTermsModalOpen}>
        <HighZIndexDialogContent className="max-w-4xl h-[90vh] p-0 flex flex-col">
          <DialogHeader className="p-6 pb-4 border-b flex-shrink-0">
            <DialogTitle>Termos de Uso</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-6">
            <TermsOfUseContent />
          </div>
        </HighZIndexDialogContent>
      </Dialog>
    </>
  );
};
